"use client"

import * as React from "react"
import { createPortal } from "react-dom"

import { cn } from "@/lib/utils"

export type Side = "top" | "right" | "bottom" | "left"
export type Align = "start" | "center" | "end"

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") {
    ref(value)
    return
  }

  if (ref) {
    ;(ref as React.MutableRefObject<T | null>).current = value
  }
}

export function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (value: T | null) => {
    refs.forEach((ref) => assignRef(ref, value))
  }
}

function isEventHandler(key: string, value: unknown) {
  return key.startsWith("on") && typeof value === "function"
}

type SlotProps = React.HTMLAttributes<HTMLElement> & {
  children?: React.ReactNode
}

export const Slot = React.forwardRef<HTMLElement, SlotProps>(
  function Slot({ children, ...slotProps }, forwardedRef) {
    if (!React.isValidElement(children)) {
      return null
    }

    const childProps = (children.props ?? {}) as Record<string, unknown>
    const mergedProps: Record<string, unknown> = { ...slotProps, ...childProps }

    Object.entries(slotProps).forEach(([key, value]) => {
      if (key === "children") return

      if (key === "className") {
        mergedProps.className = cn(
          value as string | undefined,
          childProps.className as string | undefined
        )
        return
      }

      if (key === "style") {
        mergedProps.style = {
          ...(value as React.CSSProperties | undefined),
          ...(childProps.style as React.CSSProperties | undefined),
        }
        return
      }

      if (isEventHandler(key, value) && isEventHandler(key, childProps[key])) {
        const slotHandler = value as (...args: unknown[]) => void
        const childHandler = childProps[key] as (...args: unknown[]) => void
        mergedProps[key] = (...args: unknown[]) => {
          childHandler(...args)
          slotHandler(...args)
        }
      }
    })

    return React.cloneElement(
      children as React.ReactElement<Record<string, unknown>>,
      {
        ...mergedProps,
        ref: mergeRefs(
          forwardedRef,
          (children as React.ReactElement & { ref?: React.Ref<HTMLElement> }).ref
        ),
      } as Record<string, unknown>
    )
  }
)

type UseControllableStateOptions<T> = {
  prop?: T
  defaultProp?: T
  onChange?: (value: T) => void
}

export function useControllableState<T>({
  prop,
  defaultProp,
  onChange,
}: UseControllableStateOptions<T>) {
  const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultProp)
  const isControlled = prop !== undefined
  const value = isControlled ? prop : uncontrolledValue

  const setValue = React.useCallback(
    (nextValue: React.SetStateAction<T | undefined>) => {
      const resolvedValue =
        typeof nextValue === "function"
          ? (nextValue as (value: T | undefined) => T | undefined)(value)
          : nextValue

      if (!isControlled) {
        setUncontrolledValue(resolvedValue)
      }

      if (!Object.is(value, resolvedValue) && resolvedValue !== undefined) {
        onChange?.(resolvedValue)
      }
    },
    [isControlled, onChange, value]
  )

  return [value, setValue] as const
}

export function ClientPortal({
  children,
  container,
}: {
  children: React.ReactNode
  container?: Element | null
}) {
  const [mounted, setMounted] = React.useState(false)

  React.useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) return null

  return createPortal(children, container ?? document.body)
}

type ScrollLockState = {
  count: number
  previousOverflow: string
  previousPaddingRight: string
}

const scrollLockStates = new WeakMap<HTMLElement, ScrollLockState>()

function measureScrollbarWidth(target: HTMLElement) {
  if (target === document.body) {
    return Math.max(0, window.innerWidth - document.documentElement.clientWidth)
  }

  return Math.max(0, target.offsetWidth - target.clientWidth)
}

export function useScrollLock(locked: boolean, target: HTMLElement | null) {
  React.useEffect(() => {
    if (!locked || !target) return

    const scrollElement = target
    const scrollElementStyle = scrollElement.style
    const existingState = scrollLockStates.get(scrollElement)
    if (existingState) {
      existingState.count += 1
      return () => {
        existingState.count = Math.max(0, existingState.count - 1)
        if (existingState.count === 0) {
          if (existingState.previousOverflow) {
            scrollElementStyle.setProperty("overflow", existingState.previousOverflow)
          } else {
            scrollElementStyle.removeProperty("overflow")
          }
          if (existingState.previousPaddingRight) {
            scrollElementStyle.setProperty(
              "padding-right",
              existingState.previousPaddingRight
            )
          } else {
            scrollElementStyle.removeProperty("padding-right")
          }
          scrollLockStates.delete(scrollElement)
        }
      }
    }

    const computedPaddingRight = Number.parseFloat(
      window.getComputedStyle(scrollElement).paddingRight || "0"
    )
    const scrollbarWidth = measureScrollbarWidth(scrollElement)
    const nextState: ScrollLockState = {
      count: 1,
      previousOverflow: scrollElementStyle.overflow,
      previousPaddingRight: scrollElementStyle.paddingRight,
    }

    scrollLockStates.set(scrollElement, nextState)
    scrollElementStyle.setProperty("overflow", "hidden")
    if (scrollbarWidth > 0) {
      scrollElementStyle.setProperty(
        "padding-right",
        `${computedPaddingRight + scrollbarWidth}px`
      )
    }

    return () => {
      const state = scrollLockStates.get(scrollElement)
      if (!state) return

      state.count = Math.max(0, state.count - 1)
      if (state.count === 0) {
        if (state.previousOverflow) {
          scrollElementStyle.setProperty("overflow", state.previousOverflow)
        } else {
          scrollElementStyle.removeProperty("overflow")
        }
        if (state.previousPaddingRight) {
          scrollElementStyle.setProperty("padding-right", state.previousPaddingRight)
        } else {
          scrollElementStyle.removeProperty("padding-right")
        }
        scrollLockStates.delete(scrollElement)
      }
    }
  }, [locked, target])
}

export function useBodyScrollLock(locked: boolean) {
  const target = typeof document === "undefined" ? null : document.body
  useScrollLock(locked, target)
}

export function useEscapeKey(enabled: boolean, onEscape: () => void) {
  React.useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onEscape()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [enabled, onEscape])
}

export function useDismissableLayer({
  enabled,
  refs,
  onDismiss,
}: {
  enabled: boolean
  refs: Array<React.RefObject<HTMLElement | null>>
  onDismiss: () => void
}) {
  React.useEffect(() => {
    if (!enabled) return

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null
      if (!target) return

      const isInside = refs.some((ref) => ref.current?.contains(target))
      if (!isInside) {
        onDismiss()
      }
    }

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as Node | null
      if (!target) return

      const isInside = refs.some((ref) => ref.current?.contains(target))
      if (!isInside) {
        onDismiss()
      }
    }

    document.addEventListener("pointerdown", handlePointerDown)
    document.addEventListener("focusin", handleFocusIn)

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown)
      document.removeEventListener("focusin", handleFocusIn)
    }
  }, [enabled, onDismiss, refs])
}

function getCrossAxisPosition(
  align: Align,
  anchorStart: number,
  anchorSize: number,
  contentSize: number
) {
  if (align === "start") return anchorStart
  if (align === "end") return anchorStart + anchorSize - contentSize
  return anchorStart + (anchorSize - contentSize) / 2
}

const OPPOSITE_SIDE: Record<Side, Side> = {
  top: "bottom",
  right: "left",
  bottom: "top",
  left: "right",
}

function getSideSpace(side: Side, rect: DOMRect, viewport: { width: number; height: number }) {
  if (side === "top") return rect.top
  if (side === "bottom") return viewport.height - rect.bottom
  if (side === "left") return rect.left
  return viewport.width - rect.right
}

function getMainAxisSize(side: Side, rect: DOMRect) {
  return side === "top" || side === "bottom" ? rect.height : rect.width
}

export function useAnchoredPosition({
  open,
  anchorRef,
  contentRef,
  side = "bottom",
  align = "center",
  sideOffset = 4,
}: {
  open: boolean
  anchorRef: React.RefObject<HTMLElement | null>
  contentRef: React.RefObject<HTMLElement | null>
  side?: Side
  align?: Align
  sideOffset?: number
}) {
  const [position, setPosition] = React.useState<{
    left: number
    top: number
    side: Side
  }>({
    left: -9999,
    top: -9999,
    side,
  })

  React.useLayoutEffect(() => {
    if (!open) return

    const updatePosition = () => {
      const anchor = anchorRef.current
      const content = contentRef.current
      if (!anchor || !content) return

      const margin = 8
      const viewport = { width: window.innerWidth, height: window.innerHeight }
      const anchorRect = anchor.getBoundingClientRect()
      const contentRect = content.getBoundingClientRect()
      const preferredSide = side
      const oppositeSide = OPPOSITE_SIDE[preferredSide]
      const preferredSpace = getSideSpace(preferredSide, anchorRect, viewport)
      const oppositeSpace = getSideSpace(oppositeSide, anchorRect, viewport)
      const requiredSize = getMainAxisSize(preferredSide, contentRect) + sideOffset + margin
      const resolvedSide =
        preferredSpace < requiredSize && oppositeSpace > preferredSpace
          ? oppositeSide
          : preferredSide

      let top = 0
      let left = 0

      if (resolvedSide === "bottom") {
        top = anchorRect.bottom + sideOffset
        left = getCrossAxisPosition(
          align,
          anchorRect.left,
          anchorRect.width,
          contentRect.width
        )
      } else if (resolvedSide === "top") {
        top = anchorRect.top - contentRect.height - sideOffset
        left = getCrossAxisPosition(
          align,
          anchorRect.left,
          anchorRect.width,
          contentRect.width
        )
      } else if (resolvedSide === "right") {
        left = anchorRect.right + sideOffset
        top = getCrossAxisPosition(
          align,
          anchorRect.top,
          anchorRect.height,
          contentRect.height
        )
      } else {
        left = anchorRect.left - contentRect.width - sideOffset
        top = getCrossAxisPosition(
          align,
          anchorRect.top,
          anchorRect.height,
          contentRect.height
        )
      }

      const maxLeft = Math.max(margin, viewport.width - contentRect.width - margin)
      const maxTop = Math.max(margin, viewport.height - contentRect.height - margin)

      setPosition({
        left: Math.min(Math.max(left, margin), maxLeft),
        top: Math.min(Math.max(top, margin), maxTop),
        side: resolvedSide,
      })
    }

    updatePosition()

    const scheduleUpdate = () => {
      window.requestAnimationFrame(updatePosition)
    }

    window.addEventListener("resize", scheduleUpdate)
    window.addEventListener("scroll", scheduleUpdate, true)

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => scheduleUpdate())

    if (anchorRef.current) resizeObserver?.observe(anchorRef.current)
    if (contentRef.current) resizeObserver?.observe(contentRef.current)

    return () => {
      window.removeEventListener("resize", scheduleUpdate)
      window.removeEventListener("scroll", scheduleUpdate, true)
      resizeObserver?.disconnect()
    }
  }, [align, anchorRef, contentRef, open, side, sideOffset])

  return {
    side: position.side,
    style: {
      position: "fixed" as const,
      left: position.left,
      top: position.top,
      maxWidth: "calc(100vw - 16px)",
      maxHeight: "calc(100vh - 16px)",
    },
  }
}
